import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  ChevronDown, ChevronUp, ChevronsDown, ChevronsUp,
  ExternalLink, Search, Newspaper, Activity, Calendar,
} from 'lucide-react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { getCachedNewsData } from '../utils/newsCache';
import { renderNewsRating } from '../utils/renderNewsRating';
import { isLatestDateListAtToday } from '../utils/marketTradeDates';

const ITEMS_PER_PAGE = 10;

/** One row per news_id (API can return duplicates); keep newest pub_datetime. */
function dedupeNewsById(items) {
  if (!Array.isArray(items) || items.length === 0) return [];
  const byId = new Map();
  for (const item of items) {
    if (!item?.news_id) continue;
    const existing = byId.get(item.news_id);
    if (!existing || new Date(item.pub_datetime) >= new Date(existing.pub_datetime)) {
      byId.set(item.news_id, item);
    }
  }
  return [...byId.values()].sort(
    (a, b) => new Date(b.pub_datetime) - new Date(a.pub_datetime)
  );
}

function chunkPairs(items) {
  const rows = [];
  for (let i = 0; i < items.length; i += 2) {
    rows.push(items.slice(i, i + 2));
  }
  return rows;
}

function formatNewsDate(pubDatetime) {
  if (!pubDatetime) return '—';
  return new Date(pubDatetime).toLocaleString('en-CA', {
    timeZone: 'America/New_York',
    hour12: false,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function truncateText(text, maxLen) {
  if (!text || text.length <= maxLen) return text || '';
  return `${text.substring(0, maxLen)}…`;
}

function renderSentiment(newsItem, expanded) {
  if (newsItem.rating === null || newsItem.rating === undefined) {
    return <span className="news-sentiment-empty">No sentiment data</span>;
  }

  const renderBullets = (arr) => {
    if (!arr || !Array.isArray(arr)) return null;
    return (
      <ul className="news-sentiment-list">
        {arr.map((item, index) => (
          <li key={index}>
            <span className="sentiment-strong">{item.bullet_point}:</span> {item.details}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="news-sentiment-body">
      <div className="sentiment-item sentiment-item--rating">
        <span className="sentiment-strong">Rating:</span>
        <span className="news-rating-box">{renderNewsRating(newsItem.rating)}</span>
        {newsItem.rating_tag && (
          <span className="sentiment-rating-tag">({newsItem.rating_tag})</span>
        )}
      </div>
      <div className="sentiment-item">
        <span className="sentiment-strong">Company:</span>{' '}
        {newsItem.symbol1 === newsItem.report_on ? newsItem.coy_name1 : newsItem.coy_name2}
        {newsItem.valuation && (
          <> | <span className="sentiment-strong">Total Offer:</span> {newsItem.valuation}</>
        )}
        {newsItem.share_premium && (
          <> | <span className="sentiment-strong">Share Pricing:</span> {newsItem.share_premium}</>
        )}
      </div>
      <div className="sentiment-section">
        <span className="sentiment-strong">Key Drivers:</span>
        {renderBullets(newsItem.key_drivers)}
      </div>

      {expanded && (
        <>
          {newsItem.positive_factors?.length > 0 && (
            <div className="sentiment-section">
              <span className="sentiment-strong">Positive Factors:</span>
              {renderBullets(newsItem.positive_factors)}
            </div>
          )}
          {newsItem.neutral_factors?.length > 0 && (
            <div className="sentiment-section">
              <span className="sentiment-strong">Neutral Factors:</span>
              {renderBullets(newsItem.neutral_factors)}
            </div>
          )}
          {newsItem.negative_factors?.length > 0 && (
            <div className="sentiment-section">
              <span className="sentiment-strong">Negative Factors:</span>
              {renderBullets(newsItem.negative_factors)}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function NewsCard({ news, expanded, onToggle }) {
  const stockPath = `/stock/${encodeURIComponent(String(news.symbol_id).toUpperCase())}`;
  const headline = truncateText(news.headline, 200);
  const excerpt = truncateText(news.content, expanded ? 400 : 230);
  const hasSentiment = news.rating !== null && news.rating !== undefined;

  const handleToggle = () => onToggle(news.news_id);

  const handleSentimentKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    }
  };

  return (
    <article className={`news-card${expanded ? ' news-card--expanded' : ''}`} aria-labelledby={`news-${news.news_id}-title`}>
      <div className="news-card-header">
        <div className="news-card-header-left">
          <a
            href={stockPath}
            target="_blank"
            rel="noopener noreferrer"
            className="msg-symbol-chip"
            title="View stock details"
          >
            {news.symbol}
          </a>
          {hasSentiment && (
            <span className="msg-badge msg-badge--news">
              <Activity size={11} aria-hidden />
              AI Sentiment
            </span>
          )}
        </div>
        <time className="msg-card-date" dateTime={news.pub_datetime}>
          {formatNewsDate(news.pub_datetime)}
        </time>
      </div>

      <Link
        id={`news-${news.news_id}-title`}
        to={news.weburl}
        target="_blank"
        rel="noopener noreferrer"
        className="news-card-headline"
      >
        {headline}
        <ExternalLink size={14} className="news-card-headline-icon" aria-hidden />
      </Link>

      <p className="news-card-excerpt">{excerpt}</p>

      <div
        className={`news-sentiment-container news-sentiment-container--toggleable${expanded ? ' news-sentiment-container--expanded' : ''}`}
        onClick={handleToggle}
        onKeyDown={handleSentimentKeyDown}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={expanded ? 'Collapse sentiment analysis' : 'Expand sentiment analysis'}
      >
        <div className="news-sentiment-header">
          <Activity size={15} className="news-sentiment-icon" aria-hidden />
          <span className="news-sentiment-label">Sentiment Analysis</span>
        </div>
        <div className="news-sentiment-text">
          {renderSentiment(news, expanded)}
        </div>
        <div className="news-sentiment-toggle-hint" aria-hidden="true">
          {expanded ? <ChevronsUp size={18} /> : <ChevronsDown size={18} />}
        </div>
      </div>

      <div className="news-card-actions">
        <button
          type="button"
          onClick={handleToggle}
          className="msg-action-btn msg-action-btn--ghost"
          aria-expanded={expanded}
        >
          {expanded ? <>Less <ChevronsUp size={14} /></> : <>More <ChevronsDown size={14} /></>}
        </button>
        <Link
          to={news.weburl}
          target="_blank"
          rel="noopener noreferrer"
          className="msg-action-btn msg-action-btn--primary"
        >
          Read <ExternalLink size={14} />
        </Link>
      </div>
    </article>
  );
}

function NewsPub() {
  const [expandedNews, setExpandedNews] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [newsData, setNewsData] = useState([]);
  const [searchVals, setSearchVals] = useState("");
  const [result, setResult] = useState("");
  const [availableDates, setAvailableDates] = useState([]);
  const [newsDate, setNewsDate] = useState(null);
  const [searchFields, setSearchFields] = useState({
    all: true,
    headline: false,
    symbol: false,
    sentiment: false,
    content: false,
    rating: false
  });
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    if (availableDates.length > 0) {
      setNewsDate(availableDates[0]);
    }
  }, [availableDates]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.news-search-dropdown-container')) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  useEffect(() => {
    const CACHE_TTL = 2 * 60 * 1000;
    const currentDate = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York'});
    let cancelled = false;

    const fetchNews = async (useCache = true) => {
      try {
        if (!newsDate) return;
        const formattedDate = newsDate.toLocaleDateString('en-CA', { timeZone: 'America/New_York'});

        const data = await getCachedNewsData(formattedDate, useCache);
        if (cancelled) return;
        setNewsData(dedupeNewsById(data));
      } catch (error) {
        if (!cancelled) {
          console.error('Error fetching news:', error);
        }
      }
    };

    fetchNews(true);

    const formattedNewsDate = newsDate?.toLocaleDateString('en-CA', { timeZone: 'America/New_York'});

    let refreshInterval;
    if (formattedNewsDate === currentDate) {
      refreshInterval = setInterval(() => {
        fetchNews(true);
      }, CACHE_TTL);
    }

    return () => {
      cancelled = true;
      if (refreshInterval) clearInterval(refreshInterval);
    };
  }, [newsDate]);

  useEffect(() => {
    const fetchNewsDates = async () => {
      const loadCachedDatesFallback = () => {
        try {
          const cachedDates = localStorage.getItem('newsPubDates');
          if (!cachedDates) return;
          const parsed = JSON.parse(cachedDates);
          if (!Array.isArray(parsed) || parsed.length === 0) return;
          const parsedDates = parsed.map(dateStr => {
            const [year, month, day] = dateStr.split('-');
            return new Date(year, month - 1, day, 12, 0, 0);
          });
          setAvailableDates(parsedDates);
        } catch (fallbackError) {
          console.warn('Error loading cached news dates fallback:', fallbackError);
        }
      };

      try {
        const cachedRaw = localStorage.getItem('newsPubDates');
        if (cachedRaw) {
          const parsed = JSON.parse(cachedRaw);
          if (isLatestDateListAtToday(parsed)) {
            console.log('[NEWS PUB DATES] Frozen — latest pub_date is current NY date, skipping API');
            loadCachedDatesFallback();
            return;
          }
        }

        const response = await fetch('/api/news_dates');
        if (!response.ok) {
          throw new Error('Failed to fetch news dates');
        }
        const dates = await response.json();
        if (Array.isArray(dates) && dates.length > 0) {
          const dateObjects = dates.map(dateStr => {
            const [year, month, day] = dateStr.split('-');
            return new Date(year, month - 1, day, 12, 0, 0);
          });
          setAvailableDates(dateObjects);
          localStorage.setItem('newsPubDates', JSON.stringify(dates));
        } else {
          loadCachedDatesFallback();
        }
      } catch (error) {
        console.error('Error fetching news dates:', error);
        loadCachedDatesFallback();
      }
    };

    fetchNewsDates();
  }, []);

  const getFilter = useCallback((value) => {
    if (!searchVals) return true;

    const searchTermLower = searchVals.toLowerCase();

    const searchSentiment = (newsItem) => {
      if (!newsItem) return false;

      const basicFields = ['rating', 'rating_tag', 'report_on', 'coy_name2', 'beat', 'valuation', 'share_premium', 'coy_name1', 'symbol1', 'symbol2'];
      const basicFieldsMatch = basicFields.some(field =>
        newsItem[field] && newsItem[field].toString().toLowerCase().includes(searchTermLower)
      );
      if (basicFieldsMatch) return true;

      const arrayFields = ['key_drivers', 'positive_factors', 'neutral_factors', 'negative_factors'];
      return arrayFields.some(field => {
        if (!newsItem[field] || !Array.isArray(newsItem[field])) return false;
        return newsItem[field].some(item =>
          (item.bullet_point && item.bullet_point.toLowerCase().includes(searchTermLower)) ||
          (item.details && item.details.toLowerCase().includes(searchTermLower))
        );
      });
    };

    if (searchFields.all) {
      return (
        value.headline.toLowerCase().includes(searchTermLower) ||
        (value.symbol_id && value.symbol_id.toLowerCase().includes(searchTermLower)) ||
        value.symbol.toLowerCase().includes(searchTermLower) ||
        searchSentiment(value) ||
        value.content.toLowerCase().includes(searchTermLower) ||
        value.news_id.toLowerCase().includes(searchTermLower) ||
        (value.rating && value.rating.toString().toLowerCase().includes(searchTermLower))
      );
    }

    let matches = false;

    if (searchFields.headline && value.headline.toLowerCase().includes(searchTermLower)) {
      matches = true;
    }
    if (searchFields.symbol && (
      (value.symbol_id && value.symbol_id.toLowerCase().includes(searchTermLower)) ||
      value.symbol.toLowerCase().includes(searchTermLower)
    )) {
      matches = true;
    }
    if (searchFields.sentiment && searchSentiment(value)) {
      matches = true;
    }
    if (searchFields.content && value.content.toLowerCase().includes(searchTermLower)) {
      matches = true;
    }
    if (searchFields.news_id && value.news_id.toLowerCase().includes(searchTermLower)) {
      matches = true;
    }
    if (searchFields.rating && value.rating && value.rating.toString().toLowerCase().includes(searchTermLower)) {
      matches = true;
    }

    return matches;
  }, [searchVals, searchFields]);

  function handleSubmit(e) {
    e.preventDefault();
    setSearchVals(result.trim());
    setCurrentPage(1);
  }

  function handleChange(e) {
    setResult(e.target.value);
  }

  const handleFieldChange = (field) => {
    setCurrentPage(1);
    if (field === 'all') {
      setSearchFields({
        all: true,
        headline: false,
        symbol: false,
        sentiment: false,
        content: false,
        news_id: false,
        rating: false
      });
    } else {
      setSearchFields(prev => ({
        ...prev,
        all: false,
        [field]: !prev[field]
      }));
    }
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const getSelectedFieldsText = () => {
    if (searchFields.all) return 'Default';
    const selected = Object.keys(searchFields).filter(key => key !== 'all' && searchFields[key]);
    if (selected.length === 0) return 'No fields';
    if (selected.length === 1) return selected[0].charAt(0).toUpperCase() + selected[0].slice(1);
    return `${selected.length} fields`;
  };

  const toggleContent = (id) => {
    setExpandedNews(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const filteredData = useMemo(
    () => dedupeNewsById(newsData).filter(getFilter),
    [newsData, getFilter]
  );

  const totalPages = filteredData.length === 0
    ? 1
    : Math.ceil(filteredData.length / ITEMS_PER_PAGE);

  const pageForDisplay = Math.min(Math.max(1, currentPage), totalPages);

  useEffect(() => {
    setCurrentPage(1);
    setExpandedNews({});
  }, [searchVals, searchFields, newsDate]);

  useEffect(() => {
    if (filteredData.length > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages, filteredData.length]);

  const handleChangePage = (page) => {
    const nextPage = Math.min(Math.max(1, page), totalPages);
    setCurrentPage(nextPage);
    setExpandedNews({});
  };

  const pageStart = (pageForDisplay - 1) * ITEMS_PER_PAGE;
  const displayedNews = useMemo(
    () => filteredData.slice(pageStart, pageStart + ITEMS_PER_PAGE),
    [filteredData, pageStart]
  );
  const useTwoColumns = displayedNews.length > 1;
  const gridResetKey = `${pageForDisplay}-${searchVals}-${newsDate?.toISOString() ?? ''}`;

  const newsSummary = useMemo(() => {
    const withSentiment = filteredData.filter(n => n.rating != null).length;
    return { total: filteredData.length, withSentiment };
  }, [filteredData]);

  const formattedSelectedDate = newsDate
    ? newsDate.toLocaleDateString('en-CA', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric' })
    : '—';

  return (
    <div className="page-content news-page pw-page">
      <div className="pw-header">
        <div className="pw-header-left">
          <div className="pw-header-text">
            <h2 className="pw-title">
              <Newspaper size={24} className="pw-title-icon pw-title-icon--news" />
              <span>Press Releases</span>
            </h2>
            <p className="pw-subtitle">AI-analyzed market news &amp; sentiment for your research</p>
          </div>
        </div>
      </div>

      {newsData.length > 0 && (
        <div className="news-summary-card">
          <div className="news-summary-main">
            <span className="news-summary-label">
              <Calendar size={14} aria-hidden />
              {formattedSelectedDate}
            </span>
            <span className="news-summary-value">{newsSummary.total}</span>
            <span className="news-summary-suffix">
              {searchVals
                ? (newsSummary.total === 1 ? 'matching release' : 'matching releases')
                : (newsSummary.total === 1 ? 'release' : 'releases found')}
            </span>
          </div>
          <div className="news-summary-stats">
            <div className="news-summary-stat">
              <span className="news-summary-stat-label">With AI Sentiment</span>
              <span className="news-summary-stat-value">{newsSummary.withSentiment}</span>
            </div>
            <div className="news-summary-stat">
              <span className="news-summary-stat-label">Total Loaded</span>
              <span className="news-summary-stat-value">{newsData.length}</span>
            </div>
          </div>
        </div>
      )}

      <div className="pw-data-card news-data-card">
        <div className="pw-data-header">
          <div className="pw-data-header-left">
            <h3>Daily Releases</h3>
            <span className="pw-data-count">{filteredData.length} total</span>
          </div>
          <div className="pw-data-header-right news-toolbar">
            <div className="news-toolbar-date">
              <label htmlFor="news-date-picker" className="news-toolbar-date-label">Date</label>
              <div className="date-picker-wrapper news-date-picker-wrap">
                <DatePicker
                  id="news-date-picker"
                  selected={newsDate}
                  onChange={date => {
                    const newDate = new Date(date);
                    newDate.setHours(12, 0, 0, 0);
                    setNewsDate(newDate);
                    localStorage.setItem('newsDate', newDate.toISOString());
                  }}
                  includeDates={availableDates}
                  dateFormat="yyyy-MM-dd"
                  placeholderText="Select date"
                  filterDate={date => availableDates.some(d =>
                    d.toDateString() === date.toDateString()
                  )}
                  showMonthDropdown
                  showYearDropdown
                  className="date-picker news-date-picker"
                />
                <ChevronDown className="date-picker-icon" aria-hidden />
              </div>
            </div>

            <form onSubmit={handleSubmit} className="news-search-form">
              <div className="news-search-with-dropdown">
                <div className="news-search-bar">
                  <Search size={16} className="news-search-icon" aria-hidden />
                  <input
                    className="news-search-input"
                    placeholder="Search headline, symbol, content…"
                    type="text"
                    value={result}
                    onChange={handleChange}
                    aria-label="Search news"
                  />
                  {result && (
                    <button
                      type="button"
                      className="news-search-clear"
                      onClick={() => { setResult(''); setSearchVals(''); setCurrentPage(1); }}
                      aria-label="Clear search"
                    >
                      ×
                    </button>
                  )}
                </div>
                <div className="news-search-dropdown-container">
                  <button
                    type="button"
                    className="news-search-dropdown-button"
                    onClick={toggleDropdown}
                    aria-expanded={dropdownOpen}
                    aria-haspopup="true"
                  >
                    <span className="news-search-dropdown-label">{getSelectedFieldsText()}</span>
                    {dropdownOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                  {dropdownOpen && (
                    <div className="news-search-dropdown-menu" role="menu">
                      {[
                        ['all', 'Default'],
                        ['headline', 'Headline'],
                        ['symbol', 'Symbol'],
                        ['rating', 'Rating'],
                        ['sentiment', 'Sentiment'],
                        ['content', 'Content'],
                      ].map(([field, label]) => (
                        <label key={field} className="news-search-dropdown-item">
                          <input
                            type="checkbox"
                            checked={searchFields[field]}
                            onChange={() => handleFieldChange(field)}
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>

        {filteredData.length === 0 ? (
          <div className="pw-empty-state news-empty-state">
            <Newspaper size={40} aria-hidden />
            <h3>{newsData.length === 0 ? 'No Releases Yet' : 'No Matching Releases'}</h3>
            <p>
              {newsData.length === 0
                ? 'Select a date with available press releases to view AI sentiment analysis.'
                : 'Try adjusting your search terms or date selection.'}
            </p>
          </div>
        ) : (
          <>
            <div
              key={gridResetKey}
              className={`news-grid${useTwoColumns ? ' news-grid--two-col' : ''}`}
            >
              {useTwoColumns
                ? chunkPairs(displayedNews).map((pair, rowIndex) => (
                    <div
                      className="news-grid-row"
                      key={`${gridResetKey}-row-${rowIndex}`}
                    >
                      {pair.map((news) => (
                        <NewsCard
                          key={`${gridResetKey}-${news.news_id}`}
                          news={news}
                          expanded={!!expandedNews[news.news_id]}
                          onToggle={toggleContent}
                        />
                      ))}
                    </div>
                  ))
                : displayedNews.map((news) => (
                    <NewsCard
                      key={`${gridResetKey}-${news.news_id}`}
                      news={news}
                      expanded={!!expandedNews[news.news_id]}
                      onToggle={toggleContent}
                    />
                  ))}
            </div>

            {filteredData.length > ITEMS_PER_PAGE && (
              <div className="pw-pagination">
                <button type="button" onClick={() => handleChangePage(1)} disabled={pageForDisplay === 1} title="First Page" aria-label="First page">
                  <ChevronsLeft size={18} />
                </button>
                <button type="button" onClick={() => handleChangePage(pageForDisplay - 1)} disabled={pageForDisplay === 1} title="Previous Page" aria-label="Previous page">
                  <ChevronLeft size={18} />
                </button>
                <span className="pw-page-info">Page {pageForDisplay} of {totalPages}</span>
                <button type="button" onClick={() => handleChangePage(pageForDisplay + 1)} disabled={pageForDisplay >= totalPages} title="Next Page" aria-label="Next page">
                  <ChevronRight size={18} />
                </button>
                <button type="button" onClick={() => handleChangePage(totalPages)} disabled={pageForDisplay >= totalPages} title="Last Page" aria-label="Last page">
                  <ChevronsRight size={18} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default NewsPub;
